import { SafeError, SafePromise } from '@benzinga/safe-await';
import { Subscribable, Subscription } from '@benzinga/subscribable';
import { GptSearchRequest } from './request';
import { Session } from '@benzinga/session';
import { GptSearchStore } from './store';
import { GptResponseData, GptConfigParameters, WidgetType } from './entity';
import { deepObjectMerge, oneRequestAtATimeMultipleArgs } from '@benzinga/utils';
type GptSearchErrorEvent = { error?: SafeError; type: 'gpt-search:error' };

type GptSearchUpdateEvent = { type: 'gpt-search:update'; data: GptResponseData[] };

export type GptSearchEvent = GptSearchErrorEvent | GptSearchUpdateEvent;

export class GptSearchManager extends Subscribable<GptSearchEvent> {
  private request: GptSearchRequest;
  private store: GptSearchStore;
  private requestSubscription?: Subscription<GptSearchRequest>;

  private getConfigInternal = oneRequestAtATimeMultipleArgs(
    async <T extends object>(
      config: GptConfigParameters<T>,
      widgetType: WidgetType,
      widgetId?: string,
    ): SafePromise<GptResponseData<T>> => {
      const cached = this.store.getCachedWithPromptData(config.prompt, widgetId) as GptResponseData<T> | undefined;
      if (cached)
        return { ok: { completions: deepObjectMerge(config.prev_config, cached.completions), prompt: config.prompt } };

      const requestFunc = this.getRequestFuncBasedOnWidgetType(widgetType);
      const response = await requestFunc(config);
      if (response.err) {
        this.dispatch({ error: response.err, type: 'gpt-search:error' });
        return { err: response.err };
      }

      this.store.updatePromptCachedData(config.prompt, response.ok, widgetId);
      const cache = this.store.getCachedWithPromptData(config.prompt, widgetId);
      return { ok: cache as GptResponseData<T> };
    },
  );

  constructor(session: Session) {
    super();
    this.store = new GptSearchStore();
    this.request = new GptSearchRequest(session);
    this.request
      .getPromptsList()
      .then(response => {
        if (response.err) return;
        this.store.updatePromptList(response.ok);
      })
      .catch(error => {
        this.dispatch({ error, type: 'gpt-search:error' });
      });
  }
  public static getName = () => 'gpt-search';

  public getConfig = async <T extends object>(
    config: GptConfigParameters<T>,
    widgetType: WidgetType,
    widgetId?: string,
  ): SafePromise<GptResponseData<T>> => {
    if (!widgetType)
      return { err: new SafeError("This widget doesn't support GPT capabilities yet.", 'gpt-sesarch:error') };
    return await this.getConfigInternal(config, widgetType, widgetId);
  };

  public getCachedWithPromptData = <T extends object>(prompt: string): GptResponseData<T> | undefined =>
    this.store.getCachedWithPromptData(prompt) as GptResponseData<T> | undefined;

  public getCachedData = (widgetId: string): GptResponseData[] => this.store.getCachedData(widgetId);
  public updateLastUsedConfig = (completions: object, prompt?: string, widgetId?: string) =>
    this.store.updateLastUsedConfig(completions, prompt, widgetId);
  protected onFirstSubscription = (): void => {
    this.requestSubscription = this.request.listen(event => this.dispatch(event));
  };

  protected onZeroSubscriptions = (): void => {
    this.requestSubscription?.unsubscribe();
  };

  private getRequestFuncBasedOnWidgetType = (widgetType: WidgetType) => {
    switch (widgetType) {
      case 'scanner':
        return this.request.getScannerConfig;
      case 'newsFeed':
        return this.request.getNewsFeedConfig;
    }
  };
}
